/**
 * The accounting connector (docs/11 §Integration, docs/20 D4).
 *
 * The source system routes this at `tally_sync_status` while showing Zoho
 * Books configured, which says plainly that the vendor made it pluggable and
 * named the route after the first connector they shipped. So this is a
 * catalogue of connectors rather than a Tally client: what a document becomes
 * on the way out is the connector's business, and everything else — which
 * documents are in scope, from what date, what state each one is in — is the
 * same whichever one is chosen.
 */

export type ConnectorKey = 'NONE' | 'TALLY' | 'ZOHO_BOOKS' | 'WEBHOOK'

export interface ConnectorFieldDef {
  readonly key: 'endpointUrl' | 'organisationId' | 'accessToken' | 'companyBook'
  readonly label: string
  readonly hint?: string
  readonly required: boolean
  /** Never sent back to the browser once stored. */
  readonly secret?: boolean
}

export interface ConnectorDef {
  readonly key: ConnectorKey
  readonly label: string
  readonly blurb: string
  readonly fields: readonly ConnectorFieldDef[]
}

const endpoint = (label: string, hint: string): ConnectorFieldDef =>
  ({ key: 'endpointUrl', label, hint, required: true })

export const CONNECTORS: readonly ConnectorDef[] = [
  {
    key: 'NONE',
    label: 'Not connected',
    blurb: 'Nothing is sent anywhere. Documents are marked as out of scope for accounting.',
    fields: [],
  },
  {
    key: 'TALLY',
    label: 'Tally',
    blurb:
      'Posts each document as a Tally XML voucher to a gateway on your network. '
      + 'Tally itself listens on port 9000 when "Act as Server" is on; a gateway in front of it '
      + 'is what makes that reachable from this server.',
    fields: [
      endpoint('Gateway address', 'For example http://tally-gateway.internal:9000'),
      { key: 'companyBook', label: 'Company name in Tally', hint: 'Exactly as Tally spells it.', required: true },
    ],
  },
  {
    key: 'ZOHO_BOOKS',
    label: 'Zoho Books',
    blurb: 'Posts each document to the Zoho Books API for your organisation.',
    fields: [
      endpoint('API base address', 'For example https://www.zohoapis.in/books/v3'),
      { key: 'organisationId', label: 'Organisation id', required: true },
      { key: 'accessToken', label: 'Access token', required: true, secret: true },
    ],
  },
  {
    key: 'WEBHOOK',
    label: 'Somewhere else, over a webhook',
    blurb:
      'Posts a plain JSON version of each document to an address you choose. '
      + 'Use this when the accounting system is one nobody has written a connector for.',
    fields: [
      endpoint('Address to post to', 'It must answer 2xx, and may answer with a reference.'),
      { key: 'accessToken', label: 'Bearer token', hint: 'Sent as an Authorization header.', required: false, secret: true },
    ],
  },
]

const byKey = new Map<string, ConnectorDef>(CONNECTORS.map((c) => [c.key, c]))
export const getConnectorDef = (key: string): ConnectorDef | undefined => byKey.get(key)

/**
 * The documents an accounting system cares about.
 *
 * Deliberately the money documents only: a material request or a transfer has
 * no ledger entry behind it, and pushing one would put a voucher in the books
 * for something that never touched a rupee.
 */
export const SYNCABLE_DOC_TYPES = [
  'purchase_invoice', 'sales_invoice', 'credit_note',
  'purchase_order', 'subcontract_order',
  'other_cost', 'expense_report',
] as const

export type SyncableDocType = (typeof SYNCABLE_DOC_TYPES)[number]

export const isSyncable = (docType: string): boolean =>
  (SYNCABLE_DOC_TYPES as readonly string[]).includes(docType)

/** Where one document stands with the accounting system (docs/14 §Status). */
export type IntegrationStatus =
  | 'NOT_APPLICABLE' | 'PENDING' | 'COMPLETED' | 'FAILED' | 'STOPPED' | 'OUT_OF_SCOPE'

export const INTEGRATION_STATUS_LABELS: Readonly<Record<IntegrationStatus, string>> = {
  NOT_APPLICABLE: 'Nothing to send',
  PENDING: 'Waiting to be sent',
  COMPLETED: 'In the accounting system',
  FAILED: 'Could not be sent',
  STOPPED: 'Held back by hand',
  OUT_OF_SCOPE: 'Before the start date',
}

export interface IntegrationConfigView {
  readonly connector: ConnectorKey
  readonly connectorLabel: string
  /** ISO date. Documents dated before it are never sent (docs/11 §Integration). */
  readonly fromDate: string | null
  readonly endpointUrl: string | null
  readonly organisationId: string | null
  readonly companyBook: string | null
  /** True when a token is stored. The token itself never leaves the server. */
  readonly hasAccessToken: boolean
  readonly docTypes: readonly string[]
  readonly autoSend: boolean
  readonly lastRunAt: string | null
  readonly lastRunNote: string | null
  readonly updatedAt: string | null
  readonly updatedByName: string | null
}

export interface IntegrationSummary {
  readonly configured: boolean
  readonly counts: Readonly<Record<IntegrationStatus, number>>
  readonly oldestPendingDate: string | null
}

export interface IntegrationRunResult {
  readonly attempted: number
  readonly completed: number
  readonly failed: number
  readonly note: string
}
